import { MODULES_BY_KEY, type ModuleKey } from '@ruby/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useParams } from '@tanstack/react-router';
import { ChevronDown, ChevronLeft, LayoutDashboard, LayoutGrid, Menu, PanelLeftClose, PanelLeftOpen, Search, Volume2, VolumeX, X } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { MODULE_UI } from '@/app/modules';
import { Button } from '@/components/ui/button';
import { DropdownContent, DropdownItem, DropdownLabel, DropdownMenu, DropdownTrigger } from '@/components/ui/dropdown';
import { Kbd, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { visibleMenus } from '@/lib/permissions';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { useExperience } from '@/lib/experience';
import { sound } from '@/lib/sound';
import { screenIcon } from '@/app/screen-icons';
import { InboxButton } from '../platform/inbox-button';
import { NotificationsButton } from '../platform/notifications-button';
import { BrandMark } from './brand';
import { CommandPalette, useCommandPalette } from './command-palette';
import { setLastModule } from './last-module';
import { Forbidden, NotFound } from './status-pages';
import { LiveIndicator } from './live-indicator';
import { UserMenu } from './user-menu';

/** Module workspace: its own sidebar, top bar and content area. Only this module's navigation is shown. */
export function ModuleLayout() {
  const { module } = useParams({ from: '/authed/m/$module' });
  const boot = useSession((s) => s.boot)!;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const palette = useCommandPalette();
  const params = useParams({ strict: false }) as { screen?: string };
  const path = useLocation({ select: (l) => l.pathname });
  const [closedGroups, toggleGroup] = useClosedGroups(module);

  const def = MODULES_BY_KEY[module as ModuleKey];
  const allowed = !!def && boot.modules.includes(def.key);
  useEffect(() => {
    if (allowed) setLastModule(module);
  }, [allowed, module]);
  useEffect(() => setMobileOpen(false), [params.screen]);
  useEffect(() => {
    if (params.screen) sound.nav();
  }, [params.screen]);
  // Before any early return (hook order): the menu keys this user sees.
  const badges = useNavBadges(module, allowed ? visibleMenus(boot.permissions, def.key).flatMap((m) => m.screens.filter((x) => !x.hidden).map((x) => x.key)) : []);

  if (!def) return <NotFound />;
  if (!allowed) return <Forbidden what={`the ${def.name} module`} />;

  const ui = MODULE_UI[def.key];
  const Icon = ui.icon;
  const menus = visibleMenus(boot.permissions, def.key);
  const current = menus.flatMap((m) => m.screens).find((s) => s.key === params.screen);
  // Screens shown as tabs of a hub are not listed; while one is open its hub is highlighted.
  const navMenus = menus.map((m) => ({ ...m, screens: m.screens.filter((s) => !s.hidden) })).filter((m) => m.screens.length > 0);
  const activeKey = current?.hub ?? params.screen;
  const currentMenu = def?.sectionNav ? menus.find((m) => m.screens.some((s) => s.key === params.screen)) : undefined;

  const nav = (
    <nav className="sidebar-scope flex h-full flex-col">
      <div className={cn('flex h-[72px] shrink-0 items-center gap-3 px-4', collapsed && 'lg:justify-center lg:px-0')}>
        <BrandMark className="size-10 shadow-[0_6px_18px_-4px_rgb(16_163_106/0.6)] ring-1 ring-white/15" />
        <div className={cn('min-w-0 leading-tight', collapsed && 'lg:hidden')}>
          <p className="truncate text-[16px] font-extrabold tracking-tight text-white">{boot.company.tradeName || 'Ruby Foam'}</p>
          <p className="mt-0.5 inline-flex items-center gap-1 truncate rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-200">
            <Icon className="size-3" /> {def.name}
          </p>
        </div>
        <button className="ml-auto text-fg-subtle hover:text-white lg:hidden" onClick={() => setMobileOpen(false)} aria-label="Close menu">
          <X className="size-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {def.sectionNav && <p className={cn('mt-1 mb-1 px-3 text-[11px] font-semibold tracking-wider text-fg-subtle uppercase', collapsed && 'lg:hidden')}>Overview</p>}
        <NavItem to="dashboard" module={def.key} label="Dashboard" icon={<LayoutDashboard />} active={!params.screen} collapsed={collapsed} />
        {navMenus.map((menu) => {
          // A group can be folded away; the group holding the open screen always stays open.
          const holdsActive = menu.screens.some((x) => x.key === activeKey);
          const closed = !collapsed && closedGroups.includes(menu.key) && !holdsActive;
          return (
            <div key={menu.key} className="mt-4">
              <button
                type="button"
                onClick={() => toggleGroup(menu.key)}
                aria-expanded={!closed}
                className={cn('group/g mb-1.5 flex w-full items-center gap-2 rounded-md px-3 text-[10.5px] font-bold tracking-[0.12em] text-fg-subtle uppercase transition-colors after:order-1 after:h-px after:flex-1 after:bg-white/10 hover:text-fg-muted', collapsed && 'lg:hidden')}
              >
                {menu.label}
                {/* Folded: its waiting counts stay in sight. */}
                {closed && menu.screens.some((x) => badges[x.key]) && (
                  <span className="fx-pop order-1 rounded-full bg-amber-400 px-1.5 py-px text-[10px] font-bold tracking-normal text-amber-950 tabular-nums" aria-label={`${menu.screens.reduce((t, x) => t + (badges[x.key] ?? 0), 0)} waiting`}>
                    {menu.screens.reduce((t, x) => t + (badges[x.key] ?? 0), 0)}
                  </span>
                )}
                <ChevronDown className={cn('order-2 size-3 opacity-0 transition-[transform,opacity] duration-300 group-hover/g:opacity-100 group-focus-visible/g:opacity-100', closed && '-rotate-90 opacity-70')} />
              </button>
              <div className="fx-collapse" data-closed={closed || undefined}>
                <div inert={closed || undefined}>
                  {menu.screens.map((s) => (
                    <NavItem key={s.key} to={s.key} module={def.key} label={s.navLabel ?? s.label} title={s.label} icon={(() => { const I = screenIcon(s.key); return <I />; })()} active={activeKey === s.key} collapsed={collapsed} badge={badges[s.key]} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-border p-3">
        <Link
          to="/"
          className={cn('flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-[13px] font-medium text-fg-muted transition-colors hover:bg-white/10 hover:text-white', collapsed && 'lg:justify-center lg:px-0')}
          title="All modules"
        >
          <LayoutGrid className="size-4" />
          <span className={cn(collapsed && 'lg:hidden')}>All modules</span>
        </Link>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen">
      <aside className={cn('sticky top-0 hidden h-screen shrink-0 shadow-[4px_0_24px_-12px_rgb(0_0_0/0.35)] transition-[width] duration-300 ease-out lg:block', collapsed ? 'w-[76px]' : 'w-[264px]')}>{nav}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fx-fade absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]" onClick={() => setMobileOpen(false)} />
          <aside className="fx-sheet-left absolute inset-y-0 left-0 w-72 shadow-(--shadow-pop)">{nav}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-2 border-b border-border/70 bg-bg/70 px-3 backdrop-blur-xl backdrop-saturate-150 sm:px-6">
          <Button variant="ghost" size="icon-sm" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu />
          </Button>
          <Button variant="ghost" size="icon-sm" className="fx-tip hidden lg:inline-flex" data-tip={collapsed ? 'Show menu' : 'Hide menu'} onClick={() => setCollapsed((c) => !c)} aria-label="Toggle sidebar">
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          </Button>
          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-[13px]">
            <Link to="/m/$module" params={{ module: def.key }} className="hidden text-fg-muted hover:text-fg sm:inline">
              {def.name}
            </Link>
            {currentMenu && (
              <>
                <span className="hidden text-fg-subtle sm:inline">/</span>
                <span className="hidden text-fg-muted sm:inline">{currentMenu.label}</span>
              </>
            )}
            {current && (
              <>
                <span className="hidden text-fg-subtle sm:inline">/</span>
                <span key={current.key} className="fx-rise min-w-0 font-medium break-words">{current.label}</span>
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-1.5">
            <Button variant="secondary" className="hidden h-10 w-72 justify-start rounded-xl bg-surface/70 text-fg-subtle shadow-none transition-[width,box-shadow] hover:shadow-(--shadow-card) md:inline-flex lg:focus-visible:w-80" onClick={palette.open}>
              <Search /> Find a screen… <span className="ml-auto"><Kbd>Ctrl K</Kbd></span>
            </Button>
            <SoundToggle />
            <LiveIndicator />
            <NotificationsButton />
            <InboxButton />
            <ModuleSwitcher current={def.key} />
            <UserMenu />
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-6 sm:px-6">
          <Suspense
            fallback={
              <div className="flex justify-center py-20">
                <Spinner />
              </div>
            }
          >
            {/* Each screen arrives with a short rise; filters and paging (search params) never replay it. */}
            <div key={path} className="fx-page">
              <Outlet />
            </div>
          </Suspense>
        </main>
      </div>
      <CommandPalette {...palette} />
    </div>
  );
}

/** Counts shown on menu items: vouchers waiting (pending approval + sent for rectification); inventory documents waiting for my approval. */
function useNavBadges(module: string, keys: string[]): Record<string, number | undefined> {
  const show = module === 'accounting' && keys.includes('voucher_manager');
  const q = useQuery({ queryKey: ['accounting', 'vouchers', 'stage-counts'], queryFn: () => api<Record<'pending' | 'rectification', number>>('/accounting/vouchers/stage-counts'), enabled: show, staleTime: 30_000, refetchInterval: 120_000 });
  const ch = useQuery({ queryKey: ['accounting', 'change-counts'], queryFn: () => api<{ pending: number; edited: number }>('/accounting/change-requests/counts'), enabled: show, staleTime: 30_000, refetchInterval: 120_000 });
  // Purchase & Inventory: documents waiting for my approval (owner, 2026-11-02).
  const inv = module === 'inventory' && keys.includes('approvals');
  const ia = useQuery({ queryKey: ['inventory', 'approvals', 'waiting-me'], queryFn: () => api<{ waiting: number }>('/inventory/approvals/waiting'), enabled: inv, staleTime: 30_000, refetchInterval: 120_000 });
  if (inv) return { approvals: ia.data?.waiting || undefined };
  return show && q.data ? { voucher_manager: q.data.pending + q.data.rectification + (ch.data?.pending ?? 0) || undefined } : {};
}

function NavItem({ to, module, label, title, icon, active, collapsed, badge }: { to: string; module: string; label: string; title?: string; icon?: React.ReactNode; active: boolean; collapsed: boolean; badge?: number }) {
  const cls = cn(
    'group relative my-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-medium transition-all duration-200 [&_svg]:size-[18px] [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-200',
    active
      ? 'fx-nav-active bg-linear-to-r from-emerald-400 to-teal-400 font-semibold text-emerald-950 shadow-[0_8px_22px_-6px_rgb(52_211_153/0.65)] [&_svg]:text-emerald-950'
      : 'text-sidebar-fg hover:translate-x-0.5 hover:bg-white/[.07] hover:text-white hover:[&_svg]:scale-110 [&_svg]:text-emerald-300/80',
    collapsed && 'lg:justify-center lg:px-0',
  );
  const content = (
    <>
      {icon}
      <span className={cn('min-w-0 leading-snug break-words', collapsed && 'lg:hidden')}>{label}</span>
      {!!badge && <span key={badge} className={cn('fx-pop ml-auto rounded-full px-2 py-px text-[11px] font-bold tabular-nums', active ? 'bg-emerald-950/15 text-emerald-950' : 'bg-amber-400 text-amber-950 shadow-[0_0_12px_-2px_rgb(251_191_36/0.7)]', collapsed && 'lg:hidden')} aria-label={`${badge} waiting`}>{badge}</span>}
    </>
  );
  return to === 'dashboard' ? (
    <Link to="/m/$module" params={{ module }} className={cls} title={label} activeOptions={{ exact: true }}>
      {content}
    </Link>
  ) : (
    <Link to="/m/$module/$screen" params={{ module, screen: to }} className={cls} title={title ?? label}>
      {content}
    </Link>
  );
}

/** Sounds on / off in one click (also in the user menu with theme and motion). */
function SoundToggle() {
  const { sound: on, set } = useExperience();
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-pressed={on}
      aria-label={on ? 'Sounds on — click to mute' : 'Sounds off — click to turn on'}
      data-tip={on ? 'Sounds on — click to mute' : 'Sounds off — click to turn on'}
      onClick={() => {
        set({ sound: !on });
        if (!on) setTimeout(() => sound.toggle(true), 20);
      }}
      className={cn('fx-tip', on ? 'text-primary' : 'text-fg-subtle')}
    >
      {on ? <Volume2 /> : <VolumeX />}
    </Button>
  );
}

function ModuleSwitcher({ current }: { current: ModuleKey }) {
  const modules = useSession((s) => s.boot?.modules) ?? [];
  return (
    <DropdownMenu>
      <DropdownTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="fx-tip" aria-label="Switch module" data-tip="Switch module">
          <LayoutGrid />
        </Button>
      </DropdownTrigger>
      <DropdownContent className="w-64">
        <DropdownLabel>Switch module</DropdownLabel>
        {modules.map((k) => {
          const Icon = MODULE_UI[k].icon;
          return (
            <Link key={k} to="/m/$module" params={{ module: k }}>
              <DropdownItem>
                <Icon /> <span className={cn(k === current && 'font-semibold')}>{MODULES_BY_KEY[k].name}</span>
              </DropdownItem>
            </Link>
          );
        })}
        <Link to="/">
          <DropdownItem>
            <ChevronLeft /> All modules
          </DropdownItem>
        </Link>
      </DropdownContent>
    </DropdownMenu>
  );
}

/** Sidebar menu groups this person folded away, per module (kept in this browser). */
function useClosedGroups(module: string): [string[], (key: string) => void] {
  const storeKey = `ruby.nav.closed.${module}`;
  const [closed, setClosed] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storeKey) ?? '[]') as string[];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      setClosed(JSON.parse(localStorage.getItem(storeKey) ?? '[]') as string[]);
    } catch {
      setClosed([]);
    }
  }, [storeKey]);
  const toggle = (key: string) =>
    setClosed((c) => {
      const next = c.includes(key) ? c.filter((k) => k !== key) : [...c, key];
      try {
        localStorage.setItem(storeKey, JSON.stringify(next));
      } catch {
        /* storage blocked: the choice lasts for this visit */
      }
      sound.toggle(!c.includes(key) ? false : true);
      return next;
    });
  return [closed, toggle];
}
